/**
 * backendUrl.js — Resolución centralizada del destino del backend de VEYRA.
 * 
 * Reglas de diseño y seguridad:
 * 1. En producción (process.env.NODE_ENV === "production"):
 *    - Preserva rutas relativas puras ("") para que todas las llamadas se canalicen
 *      a través del proxy inverso Nginx en el mismo dominio HTTPS en Hetzner.
 *    - Si por error quedara configurada una URL hacia localhost o 127.0.0.1,
 *      se neutraliza a "" de forma preventiva.
 * 2. En desarrollo (process.env.NODE_ENV !== "production"):
 *    - Respeta la URL configurada en process.env.REACT_APP_BACKEND_URL (.env.development).
 *    - Si por alguna razón estuviera vacía, recurre al host activo en el puerto 8001.
 * 3. Centraliza el destino para:
 *    - Cliente API HTTP (fetch/REST).
 *    - Cliente Streaming SSE (Creative Director).
 *    - Medios y miniaturas (MediaFrame / ProjectCover).
 */

const rawBase = (process.env.REACT_APP_BACKEND_URL || "").trim();
const isProd = process.env.NODE_ENV === "production";
const isLocalhost = /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?/i.test(rawBase);

export const BACKEND_BASE = isProd
  ? (isLocalhost ? "" : rawBase)
  : (rawBase || (typeof window !== "undefined" && window.location ? `${window.location.protocol}//${window.location.hostname}:8001` : "http://localhost:8001"));

/**
 * Resuelve una URL de API o medio estático asegurando el prefijo correcto
 * en desarrollo y la ruta relativa en producción.
 */
export const resolveBackendUrl = (rawUrl) => {
  if (!rawUrl || typeof rawUrl !== "string") return "";
  const trimmed = rawUrl.trim();

  // En producción, convertir URLs absolutas hacia localhost a rutas relativas
  if (isProd && /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?(\/.*)?$/i.test(trimmed)) {
    try {
      const parsed = new URL(trimmed);
      return parsed.pathname + parsed.search;
    } catch { /* no-op */ }
  }

  // URLs absolutas, data URLs o blob URLs externas se devuelven intactas
  if (
    trimmed.startsWith("http://") ||
    trimmed.startsWith("https://") ||
    trimmed.startsWith("data:") ||
    trimmed.startsWith("blob:")
  ) {
    return trimmed;
  }

  const base = BACKEND_BASE;
  return `${base}${trimmed.startsWith("/") ? "" : "/"}${trimmed}`;
};
